
import React, { useState, useContext } from 'react';
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Bell, LogOut, HelpCircle } from 'lucide-react';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { UserProfileModal } from './UserProfileModal';
import { NotificationModal } from './NotificationModal';
import { HelpModalContext } from './MainLayout';

export const Header = () => {
  const { signOut, user } = useAuth();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const helpModal = useContext(HelpModalContext);

  const { data: profile } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const handleSignOut = () => {
    signOut();
  };

  const getUserInitials = () => {
    if (profile?.full_name) {
      return profile.full_name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return user?.email?.[0].toUpperCase() || 'U';
  };

  return (
    <>
      <header className="h-16 border-b bg-white flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <SidebarTrigger />
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full flex items-center justify-center overflow-hidden">
              <img
                src="/greencard-uploads/logo.png"
                alt="GreenCard Logo"
                className="h-full w-full object-contain"
              />
            </div>
            <span className="font-semibold text-lg md:hidden">GreenCard SM</span>
            <span className="font-semibold text-lg hidden md:block">GreenCard Sustainability Manager</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setNotificationModalOpen(true)}
            className="hover:bg-gray-100"
          >
            <Bell className="h-4 w-4" />
          </Button>
          <button
            id="help-menu"
            className="rounded-full p-2 hover:bg-blue-100 transition"
            aria-label="Help & Support"
            onClick={helpModal.open}
          >
            <HelpCircle className="h-4 w-4 text-blue-600" />
          </button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setProfileModalOpen(true)}
            className="hover:bg-gray-100 p-1"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={profile?.profile_picture} />
              <AvatarFallback className="text-sm">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
          </Button>

          <Button variant="ghost" size="icon" onClick={handleSignOut}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <UserProfileModal
        open={profileModalOpen}
        onOpenChange={setProfileModalOpen}
      />

      <NotificationModal
        open={notificationModalOpen}
        onOpenChange={setNotificationModalOpen}
      />

      {helpModal.isOpen && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40 animate-fade-in">
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-2xl p-8 max-w-lg w-full animate-fade-in-slow">
            <h2 className="text-2xl font-bold mb-4 text-blue-700">Help & Support</h2>
            <p className="mb-4">Welcome to the GreenCard Help Center! Here you can find documentation, FAQs, and contact support.</p>
            <ul className="list-disc ml-6 mb-4 text-blue-900">
              <li><a href="/docs" className="underline hover:text-blue-600">Documentation</a></li>
              <li><a href="/faq" className="underline hover:text-blue-600">Frequently Asked Questions</a></li>
              <li><a href="mailto:support@greencard.com" className="underline hover:text-blue-600">Contact Support</a></li>
            </ul>
            <button onClick={helpModal.close} className="w-full glassy-btn mt-2">Close</button>
          </div>
          <style>{`
            .glassy-btn {
              background: rgba(255,255,255,0.25);
              border: 1px solid rgba(30,64,175,0.15);
              box-shadow: 0 4px 24px 0 rgba(31, 38, 135, 0.17);
              backdrop-filter: blur(8px);
              color: #2563eb;
              transition: all 0.2s cubic-bezier(.4,2,.6,1);
            }
            .glassy-btn:hover {
              background: rgba(37,99,235,0.12);
              color: #1e293b;
            }
            .animate-fade-in {
              animation: fadeIn 0.4s cubic-bezier(.4,2,.6,1);
            }
            .animate-fade-in-slow {
              animation: fadeIn 0.8s cubic-bezier(.4,2,.6,1);
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </>
  );
};
